// The form-name SUGGESTION for an unassigned lead (docs/lead-relay.md §7.1). Pure.
//
// ⚠️⚠️ A SUGGESTION, NEVER A DECISION. Since 2026-10-07 the form name does not
// route a lead (routing.ts). What it can still do is tell the person assigning
// an unassigned lead where it PROBABLY belongs: the route that lists the
// arriving form name, or the routes whose names / form names are closest. Every
// surface that shows this labels it as a suggestion — it must be impossible to
// mistake for something the system already did.

import type { LeadRouteSuggestion } from '../../types/leadRelay.js';
import { topMatches } from '../fuzzyMatch.js';
import { normalizeKey } from './normalize.js';

export interface RouteForMatching {
  id: string;
  name: string;
  active: boolean;
  metaFormNames: string[];
}

/**
 * A leadRoutes doc the relay could not use because its `provider` is one this
 * build does not know (the store leaves it out of routing).
 */
export interface UnsupportedRoute {
  id: string;
  name: string;
  /** The stored value, verbatim (it may not even be a string). */
  provider: unknown;
  metaFormNames: string[];
}

export const CLOSEST_ROUTE_LIMIT = 3;
const CLOSEST_ROUTE_THRESHOLD = 0.3;

export function suggestRoutesForFormName(
  formName: string | undefined | null,
  routes: readonly RouteForMatching[],
): LeadRouteSuggestion {
  const name = (formName ?? '').trim();
  if (name === '') return { formName: null, listedOn: [], closest: [] };
  const key = normalizeKey(name);
  const listedOn = routes
    .filter((r) => r.metaFormNames.some((n) => normalizeKey(n) === key))
    .map((r) => ({ id: r.id, name: r.name, active: r.active }));
  if (listedOn.length > 0) return { formName: name, listedOn, closest: [] };
  const closest = topMatches(name, routes, (r) => [r.name, ...r.metaFormNames], {
    threshold: CLOSEST_ROUTE_THRESHOLD,
    limit: CLOSEST_ROUTE_LIMIT,
  }).map(({ item }) => ({ id: item.id, name: item.name }));
  return { formName: name, listedOn: [], closest };
}

/** The suggestion in words — always opening with the word that makes it a hint. */
export function describeRouteSuggestion(suggestion: LeadRouteSuggestion): string {
  const name = suggestion.formName;
  if (name === null) return 'Suggestion: none — the lead arrived with no Meta form name.';
  if (suggestion.listedOn.length > 0) {
    const where = suggestion.listedOn.map((r) => (r.active ? r.name : `${r.name} (inactive)`)).join(', ');
    return `Suggestion: the form name "${name}" is listed on ${where}.`;
  }
  if (suggestion.closest.length > 0) {
    return `Suggestion: no route lists the form name "${name}"; the closest are ${suggestion.closest.map((r) => r.name).join(', ')}.`;
  }
  return `Suggestion: none — no route lists the form name "${name}", and none looks close.`;
}
